import { useParams } from 'react-router-dom';
import {
  useLocationAssistantReportShifts,
  usePTSchedule,
  useUpdateReportShiftStatus,
} from '../../utils/api-hooks/pt-schedule.hook';
import { Table, Switch, Tooltip } from 'antd/lib';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import { filter, forEach, groupBy, map, orderBy, size, times } from 'lodash';
import { useListEmployees } from '../../utils/api-hooks/employee.hook';
import { EMPLOYEE_STATE } from '../../constants/employee';

interface PTReportShiftTableProps {
  showHolidays: boolean;
}

export default function PTReportShiftTable(props: PTReportShiftTableProps) {
  const { showHolidays } = props;
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const { data: schedule, isLoading: isLoadingSchedules } = usePTSchedule(
    scheduleId!
  );
  const { data: { data: shifts } = {}, isLoading: isLoadingShifts } =
    useLocationAssistantReportShifts(schedule?.startTime, schedule?.endTime);
  const { data: { data: employees = [] } = {}, isLoading: isLoadingEmployees } =
    useListEmployees();
  const updateReportShiftStatus = useUpdateReportShiftStatus();

  const isLoading = isLoadingSchedules || isLoadingShifts || isLoadingEmployees;

  const handleStatusChange = (
    employeeId: string,
    time: string,
    newStatus: boolean
  ) => {
    updateReportShiftStatus.mutate({
      employeeId,
      time,
      status: newStatus ? 'AVAILABLE' : 'UNAVAILABLE',
    });
  };

  const dateRange = useMemo(() => {
    if (!schedule?.startTime || !schedule?.endTime) return [];

    const dayCount =
      dayjs(schedule.endTime).diff(dayjs(schedule.startTime), 'day') + 1;

    return times(dayCount, (index) => {
      const date = dayjs(schedule.startTime)
        .locale('zh-tw')
        .startOf('day')
        .add(index, 'day');

      return {
        date,
        dateString: date.toISOString(),
        isWeekend: date.day() === 0 || date.day() === 6,
        formattedDate: date.format('MM/DD(dd)'),
      };
    });
  }, [schedule?.startTime, schedule?.endTime]);

  const dataSource = useMemo(() => {
    const employeeShiftMap = groupBy(shifts, 'employeeId');

    return map(orderBy(employees, 'onBoardingTime', 'asc'), (employee) => {
      const shiftsForEmployee = employeeShiftMap[employee.id] || [];
      const shifts: Record<string, boolean> = {};

      forEach(shiftsForEmployee, ({ time, status }) => {
        shifts[time] = status === 'AVAILABLE';
      });

      return {
        ...employee,
        ...shifts,
        isReported: size(shiftsForEmployee) > 0,
      };
    });
  }, [shifts, employees]);

  return (
    <Table
      loading={isLoading}
      dataSource={filter(dataSource, ['state', EMPLOYEE_STATE.ACTIVATED]) || []}
      rowKey="id"
      size="small"
      pagination={false}
      columns={[
        {
          title: '是否已報班',
          dataIndex: 'isReported',
          align: 'center',
          width: 110,
          filterMultiple: false,
          filters: [
            { text: '已報班', value: true },
            { text: '未報班', value: false },
          ],
          onFilter: (value, record) => record.isReported === value,
          render: (value: boolean) => (value ? '✔️' : '❌'),
        },
        {
          title: '員工',
          dataIndex: 'name',
        },
        ...dateRange.map(({ dateString, formattedDate, isWeekend }) => ({
          align: 'center' as any,
          hidden: !showHolidays && isWeekend,
          title: (
            <span className={`${isWeekend ? 'text-red-500' : ''}`}>
              {formattedDate}
            </span>
          ),
          dataIndex: dateString,
          width: 140,
          filterMultiple: false,
          filters: [
            { text: '可上班', value: true },
            { text: '不可上班', value: false },
          ],
          onFilter: (value: any, record: any) => !!record[dateString] === value,
          render: (value: boolean, record: any) => (
            <Tooltip title="點擊切換狀態">
              <Switch
                size="small"
                checked={value}
                loading={updateReportShiftStatus.isLoading}
                onChange={(checked) =>
                  handleStatusChange(record.id, dateString, checked)
                }
              />
            </Tooltip>
          ),
        })),
      ]}
    />
  );
}
